import { useCallback, useMemo, useState } from 'react';
import { Table, Button, ButtonVariants, ButtonSizes } from '@carboledger/ui';
import { useTranslations, UnitTypeContext, useUnitTypeSelection } from '@/hooks';
import { LoadingOverlay } from '@/components/feedback';
import { DiscrepancyModal } from '@/components/common';
import { buildTableLabels } from '@/config/tableLabels';
import { EMPTY_LIST } from '@/lib/utils';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import { useGetLinkedShipmentsForDeclarationQuery } from '../api/declarationsApi';
import { buildDeclarationLinkedShipmentsColumns } from '../constants/declarationLinkedShipmentsColumns';
import styles from './DeclarationLinkedShipmentsTabView.module.scss';

const getLinkedShipmentId = (row) => row.id;

/**
 * Props:
 *   declarationId  the declaration whose linked shipments the tab reads
 */
export const DeclarationLinkedShipmentsTabView = ({ declarationId }) => {
  const t = useTranslations();
  const [discrepancyRow, setDiscrepancyRow] = useState(null);
  // <Table> reports sort as { id, dir }; the mapper turns it into a ShipmentOrderBy.
  const [sort, setSort] = useState(null);
  const { contextValue: unitTypeContext } = useUnitTypeSelection();

  const { data, isLoading } = useGetLinkedShipmentsForDeclarationQuery({ declarationId, sort });
  const rows = data?.rows ?? EMPTY_LIST;

  const handleOpenDiscrepancy = useCallback((row) => setDiscrepancyRow(row), []);
  const handleCloseDiscrepancy = useCallback(() => setDiscrepancyRow(null), []);

  const columns = useMemo(
    () => buildDeclarationLinkedShipmentsColumns(t, { onOpenDiscrepancy: handleOpenDiscrepancy }),
    [t, handleOpenDiscrepancy],
  );

  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  const emptyState = useMemo(
    () => ({
      image: <img src={emptyBoxIllustration} alt="" width={200} height={200} />,
      title: t('DECLARATIONS_LINKED_SHIPMENTS_EMPTY_TITLE'),
      description: t('DECLARATIONS_LINKED_SHIPMENTS_EMPTY_DESCRIPTION'),
    }),
    [t],
  );

  if (isLoading) {
    return <LoadingOverlay label={t('COMMON_LOADING')} />;
  }

  return (
    <>
      <div className={styles.tab}>
        <div className={styles.toolbar}>
          {/* TODO: enable once the declaration ⇄ shipment link exists server-side. */}
          <Button variant={ButtonVariants.secondary} size={ButtonSizes.sm} disabled>
            {t('ACTION_LINK_SHIPMENT')}
          </Button>
        </div>
        <UnitTypeContext.Provider value={unitTypeContext}>
          <Table
            columns={columns}
            data={rows}
            getRowId={getLinkedShipmentId}
            labels={tableLabels}
            emptyState={emptyState}
            searchable
            onSortChange={setSort}
          />
        </UnitTypeContext.Provider>
      </div>

      <DiscrepancyModal
        open={discrepancyRow != null}
        onClose={handleCloseDiscrepancy}
        referenceId={discrepancyRow?.shipmentId}
        discrepancies={discrepancyRow?.discrepancyItems ?? EMPTY_LIST}
      />
    </>
  );
};
